import { useCallback, useEffect, useMemo, type Dispatch, type MutableRefObject, type SetStateAction } from 'react'
import { doc, onSnapshot, updateDoc } from 'firebase/firestore'
import { firestoreDb } from '../../services/firebase'
import { WORKSPACE_DEFAULT_CHECKLIST_ID, type TimelineElement } from '../../context/TimelineContext'
import type { useRecurringLens } from '../../context/recurringLensContextCore'
import { evaluateRecurringAddEligibility } from '../../utils/recurringParentEligibility'
import { isRecurringItemVisibleOnDate } from '../../utils/recurringWindowFilter'
import type { ChecklistItem as BoardChecklistItem } from '../../types/schema'
import type { buildReciprocalDependencyLinks } from '../../utils/checklistCanvasSaveGraph'
import { findEmptyCanvasSlot, type CanvasSlotBox } from '../../services/canvasPlacementService'
import {
  GATE_DIAMETER,
  NODE_HEIGHT,
  NODE_WIDTH,
} from '../../services/canvasLayoutConstants'
import { isGateNode } from './boardItem'
import {
  getConnectedChecklistNodeIds,
  nodesToElementChecklist,
  checklistMirrorsMatch,
  isRecurringChecklistType,
} from './liveReconciliation'

export function useBoardNodesAndMirrors({
  elements,
  activeCanvasContainerId,
  recurringLens,
  canCreateChecklist,
  nodes,
  localReciprocalLinks,
  optimisticWeights,
  optimisticPositions,
  visibleContainerIds,
  canEditCanvas,
  flashLocal,
  commitLocalNodes,
  setOptimisticPositions,
  isDebugCanvas,
  setExternalWorkspaceProgressByToken,
  canEditStructure,
  hasElementMirror,
  item,
  lastChecklistMirrorRef,
  hasBoardSnapshotRef,
}: {
  elements: TimelineElement[]
  activeCanvasContainerId: string
  recurringLens: ReturnType<typeof useRecurringLens>
  canCreateChecklist: boolean
  nodes: BoardChecklistItem[]
  localReciprocalLinks: ReturnType<typeof buildReciprocalDependencyLinks>
  optimisticWeights: Record<string, number>
  optimisticPositions: Record<string, { x: number; y: number }>
  visibleContainerIds: Set<string>
  canEditCanvas: boolean
  flashLocal: (message: string) => void
  commitLocalNodes: (updater: (current: BoardChecklistItem[]) => BoardChecklistItem[]) => void
  setOptimisticPositions: Dispatch<SetStateAction<Record<string, { x: number; y: number }>>>
  isDebugCanvas: boolean
  setExternalWorkspaceProgressByToken: Dispatch<SetStateAction<Map<string, number>>>
  canEditStructure: boolean
  hasElementMirror: boolean
  item: TimelineElement
  lastChecklistMirrorRef: MutableRefObject<string>
  hasBoardSnapshotRef: MutableRefObject<boolean>
}) {
  // Parent-scoped recurring: creating a Daily / Weekly checklist requires
  // the active container to be a real Task / Goal / Subtask with both dates
  // set. At Board / Dashboard level (activeCanvasContainerId ==
  // WORKSPACE_DEFAULT_CHECKLIST_ID) or under any ineligible element, the
  // "Add new checklist" affordances hide entirely so the user isn't offered
  // an action that will only fail. This ONLY applies under the recurring
  // lens — INDIVIDUAL canvases still allow board-level add.
  const activeParentElement = useMemo(
    () => elements.find((el) => el.id === activeCanvasContainerId) ?? null,
    [activeCanvasContainerId, elements],
  )
  const recurringParentEligibility = useMemo(
    () => evaluateRecurringAddEligibility({
      underRecurringLens: Boolean(recurringLens),
      activeContainerId: activeCanvasContainerId,
      workspaceDefaultContainerId: WORKSPACE_DEFAULT_CHECKLIST_ID,
      parentElement: activeParentElement,
    }),
    [activeCanvasContainerId, activeParentElement, recurringLens],
  )
  const canAddChecklistHere = canCreateChecklist && (recurringLens ? recurringParentEligibility.ok : true)

  const shouldMirrorNodeToElement = useCallback(
    (node: Pick<BoardChecklistItem, 'checklistType' | 'parentContainerId'>) =>
      !isRecurringChecklistType(node.checklistType) && node.parentContainerId !== WORKSPACE_DEFAULT_CHECKLIST_ID,
    [],
  )

  // Board-scoped nodes: every node that lives on the CURRENT book (matching
  // checklistType + non-archived + within its recurring window), regardless
  // of which parent breadcrumb is currently focused. Layout math (overlap
  // detection for auto-placement) and cross-parent wire routing MUST use
  // this set — filtering it further by breadcrumb caused new items to be
  // dropped on top of sibling-parent nodes because those were invisible to
  // the collision check. Overlays (optimistic drag positions, reciprocal
  // link maps, live weight) are applied here so every downstream consumer
  // sees the same picture the user is dragging.
  const boardScopedNodes = useMemo(
    () => {
      const source = nodes.filter((node) => {
        const type = node.checklistType ?? 'INDIVIDUAL'
        if (recurringLens) {
          if (type !== recurringLens.mode) return false
        } else if (type !== 'INDIVIDUAL') {
          return false
        }
        if (node.isArchived) return false
        if (recurringLens) {
          if (!isRecurringItemVisibleOnDate(recurringLens.focusedDateIso, recurringLens.mode, node.recurringWindow)) {
            return false
          }
        }
        return true
      })
      return source.map((node) => ({
        ...node,
        dependencyNodeIds: node.prerequisites,
        blockedByNodeIds: localReciprocalLinks.get(node.id)?.blockedBy ?? [],
        isBlockerForNodeIds: localReciprocalLinks.get(node.id)?.blockerFor ?? [],
        weightage: optimisticWeights[node.id] ?? node.weightage,
        position: optimisticPositions[node.id] ?? node.position,
      }))
    },
    [localReciprocalLinks, nodes, optimisticPositions, optimisticWeights, recurringLens],
  )

  const displayNodes = useMemo(
    () => boardScopedNodes.filter((node) => visibleContainerIds.has(node.parentContainerId)),
    [boardScopedNodes, visibleContainerIds],
  )

  // Deduplicate overlapping nodes on the current canvas view. Walks the
  // visible (recurring-lens-scoped) set top-left-first, keeps every node in
  // place when its current position doesn't collide with what we've already
  // accepted, and only re-runs `findEmptyCanvasSlot` for the ones that do —
  // preferring their existing coordinate first so the layout doesn't shuffle
  // more than it has to. Persistence goes through the normal Save flow: this
  // just marks the board dirty, matching the "user is the authority for drag"
  // contract. Scoped strictly to nodes visible on the current view, so an
  // Individual canvas won't touch Daily/Weekly layout and vice-versa.
  const spreadCanvasNodes = useCallback(() => {
    if (!canEditCanvas) {
      flashLocal('You do not have permission to rearrange the canvas.')
      return
    }
    const scoped = boardScopedNodes
    if (scoped.length === 0) {
      flashLocal('Nothing to spread out on this canvas.')
      return
    }

    const connectedIds = getConnectedChecklistNodeIds(scoped)

    const accepted: CanvasSlotBox[] = []
    for (const node of scoped) {
      if (connectedIds.has(node.id)) {
        const gate = isGateNode(node)
        const width = gate ? GATE_DIAMETER : NODE_WIDTH
        const height = gate ? GATE_DIAMETER : NODE_HEIGHT
        const current = optimisticPositions[node.id] ?? node.position ?? { x: 0, y: 0 }
        accepted.push({ position: current, width, height })
      }
    }

    const freeOrdered = scoped
      .filter((node) => !connectedIds.has(node.id))
      .sort((a, b) => {
        const ay = optimisticPositions[a.id]?.y ?? a.position?.y ?? 0
        const by = optimisticPositions[b.id]?.y ?? b.position?.y ?? 0
        if (ay !== by) return ay - by
        const ax = optimisticPositions[a.id]?.x ?? a.position?.x ?? 0
        const bx = optimisticPositions[b.id]?.x ?? b.position?.x ?? 0
        return ax - bx
      })

    const nextById = new Map<string, { x: number; y: number }>()
    let movedCount = 0

    for (const node of freeOrdered) {
      const gate = isGateNode(node)
      const width = gate ? GATE_DIAMETER : NODE_WIDTH
      const height = gate ? GATE_DIAMETER : NODE_HEIGHT
      const current = optimisticPositions[node.id] ?? node.position ?? { x: 0, y: 0 }
      const resolved = findEmptyCanvasSlot({
        existing: accepted,
        newNode: { width, height },
        preferred: current,
      })
      if (resolved.x !== current.x || resolved.y !== current.y) movedCount += 1
      accepted.push({ position: resolved, width, height })
      nextById.set(node.id, resolved)
    }

    if (movedCount === 0) {
      flashLocal('No overlaps found on this canvas.')
      return
    }

    commitLocalNodes((current) => current.map((node) => {
      const nextPos = nextById.get(node.id)
      if (!nextPos) return node
      const currentPos = node.position ?? { x: 0, y: 0 }
      if (nextPos.x === currentPos.x && nextPos.y === currentPos.y) return node
      return { ...node, position: nextPos }
    }))
    setOptimisticPositions((prev) => {
      let changed = false
      const next = { ...prev }
      nextById.forEach((_pos, id) => {
        if (id in next) { delete next[id]; changed = true }
      })
      return changed ? next : prev
    })
    flashLocal(`Spread out ${movedCount} disconnected node${movedCount === 1 ? '' : 's'}. Hit Save to persist.`)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: setOptimisticPositions are useRef/useState identities passed from the Surface (stable)
  }, [boardScopedNodes, canEditCanvas, commitLocalNodes, flashLocal, optimisticPositions])

  // Sorted & deduped tokens rendered as a stable JOIN string so the
  // downstream Firestore-subscription effect only tears down / rebuilds
  // when the actual set of cross-workspace targets changes — not on every
  // displayNodes reference churn (which happens on every state update).
  // This eliminated a per-render onSnapshot rebuild storm that pinned CPU
  // on the Checklists page.
  const interWorkspaceTargetsKey = useMemo(() => {
    const tokens = new Set<string>()
    for (const node of displayNodes) {
      const value = node.interWorkspaceTargetId
      if (typeof value === 'string' && value.includes(':')) tokens.add(value)
    }
    return [...tokens].sort().join(',')
  }, [displayNodes])

  useEffect(() => {
    if (isDebugCanvas) {
      setExternalWorkspaceProgressByToken(new Map())
      return
    }
    if (interWorkspaceTargetsKey.length === 0) return
    const tokens = interWorkspaceTargetsKey.split(',')

    const unsubscribers = tokens.map((token) => {
      const [workspaceId, elementId] = token.split(':')
      return onSnapshot(doc(firestoreDb, 'elements', elementId), (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() as Partial<TimelineElement> : null
        const progress = data?.workspaceId === workspaceId && typeof data.progress === 'number'
          ? Math.min(100, Math.max(0, data.progress))
          : 0
        setExternalWorkspaceProgressByToken((current) => {
          const next = new Map(current)
          next.set(token, progress)
          return next
        })
      })
    })

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe())
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: setExternalWorkspaceProgressByToken are useRef/useState identities passed from the Surface (stable)
  }, [interWorkspaceTargetsKey, isDebugCanvas])

  const persistChecklistMirror = useCallback(async (boardNodes: BoardChecklistItem[]) => {
    if (!canEditStructure) return
    if (isDebugCanvas) return
    if (!hasElementMirror) return

    const nextChecklist = nodesToElementChecklist(item.id, boardNodes)
    if (checklistMirrorsMatch(item.checklist, nextChecklist)) return

    const signature = JSON.stringify(nextChecklist)
    if (lastChecklistMirrorRef.current === signature) return
    lastChecklistMirrorRef.current = signature

    await updateDoc(doc(firestoreDb, 'elements', item.id), { checklist: nextChecklist })
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: lastChecklistMirrorRef are useRef/useState identities passed from the Surface (stable)
  }, [canEditStructure, hasElementMirror, isDebugCanvas, item.checklist, item.id])

  useEffect(() => {
    if (nodes.length === 0 && item.checklist.length > 0 && !hasBoardSnapshotRef.current) return

    const timeout = window.setTimeout(() => {
      void persistChecklistMirror(displayNodes)
        .catch((error) => console.error('[ChecklistDrawingBoard] checklist mirror sync failed', error))
    }, 120)

    return () => window.clearTimeout(timeout)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- CR6.a2: hasBoardSnapshotRef are useRef/useState identities passed from the Surface (stable)
  }, [displayNodes, item.checklist.length, nodes.length, persistChecklistMirror])

  const elementById = useMemo(() => new Map(elements.map((element) => [element.id, element])), [elements])
  const nodeParentPresentation = useMemo(() => {
    const presentation = new Map<string, { path: string; color: string }>()
    nodes.forEach((node) => {
      const chain: TimelineElement[] = []
      let current = elementById.get(node.parentContainerId)
      while (current) {
        chain.unshift(current)
        current = current.parentId ? elementById.get(current.parentId) : undefined
      }
      const immediate = chain[chain.length - 1]
      presentation.set(node.id, {
        path: chain.length > 0 ? chain.map((element) => element.title).join(' / ') : 'Dashboard',
        color: immediate?.color ?? 'var(--chart-8)',
      })
    })
    return presentation
  }, [elementById, nodes])

  return {
    activeParentElement,
    recurringParentEligibility,
    canAddChecklistHere,
    shouldMirrorNodeToElement,
    boardScopedNodes,
    displayNodes,
    spreadCanvasNodes,
    interWorkspaceTargetsKey,
    persistChecklistMirror,
    elementById,
    nodeParentPresentation,
  }
}
